// The closed set of codes a user-bound `error` event carries.
//
// An `error` event reaches the person in front of the screen. What it says
// there is the client's catalog copy for its `code` (Focus renders DE/EN text
// keyed on it, `apps/web/src/focus/errorCopy.ts`), never the exception that
// caused it: an exception message is written for a developer — a stack frame,
// a host name, a token in a URL — and it is content the log contract keeps off
// the wire. The raw error stays in the server's request log.
//
// `message` still rides the event, for surfaces with no catalog (the dashboard
// toast prints it). It is the static English line below, so even that surface
// never shows an exception's text.
//
// `ErrorEvent.code` stays typed `string` on the wire (an older client, a
// recorded fixture or a replayed log may carry a code this list no longer
// names). This list is what the SERVER is allowed to emit; the server's arch
// scan (`apps/server/test/arch/user_error_emits_carry_codes.test.ts`) holds it
// to that.

import {
  MEETING_USER_ERROR_CODES,
  MEETING_USER_ERROR_FALLBACK_EN,
} from "../meetings/error-codes.js";

export const USER_ERROR_CODES = [
  // A structured frame's handler threw. Generic on purpose: the user retries.
  "request_failed",
  // The client sent a frame the server could not parse.
  "frame_invalid",
  // The socket's message handler threw past every frame branch; terminal.
  "runtime_error",
  "sandbox_not_ready",
  "budget_not_exhausted",
  "figma_preview_invalid",
  "field_not_declinable",
  // The ready-screen field-edit path (`handleReadinessFieldEdit`). The client
  // branches on these — `edit_agent_unavailable` gets its own reassuring copy.
  "edit_unavailable",
  "edit_invalid",
  "edit_after_apply",
  "edit_agent_unavailable",
  "edit_failed",
  "answer_route_failed",
  // The boot-window agent (the servant).
  "servant_turn_failed",
  "servant_turn_timeout",
  // The provider refused the model call's API key (401 / 403).
  "model_key_rejected",
  // The router, once the sandbox is gone for good.
  "sandbox_terminated",
  "assistant_not_configured",
  // Meetings, owned by `../meetings/error-codes.ts`.
  ...MEETING_USER_ERROR_CODES,
] as const;

export type UserErrorCode = (typeof USER_ERROR_CODES)[number];

const CODE_SET: ReadonlySet<string> = new Set(USER_ERROR_CODES);

export const isUserErrorCode = (v: unknown): v is UserErrorCode =>
  typeof v === "string" && CODE_SET.has(v);

/** The wire `message` for each code: static English, never an exception's
 *  text. What a surface without a catalog (the dashboard toast) prints. */
export const USER_ERROR_FALLBACK_EN: Readonly<Record<UserErrorCode, string>> = {
  request_failed: "That didn't go through — please try again.",
  frame_invalid: "Loom couldn't read that — reload the page and try again.",
  runtime_error: "Loom ran into a problem and had to stop.",
  sandbox_not_ready: "Loom isn't ready yet — try again in a moment.",
  budget_not_exhausted: "There's nothing to extend right now.",
  figma_preview_invalid:
    "Loom couldn't take that answer about the design — please try again.",
  field_not_declinable: "That field can't be skipped.",
  edit_unavailable:
    "Editing isn't available here yet — make the change in the chat instead.",
  edit_invalid: "Couldn't process that edit — please try again.",
  edit_after_apply: "This issue was already applied — reopen it to edit again.",
  edit_agent_unavailable:
    "Your edit is saved — it'll be applied as soon as your session is back up.",
  edit_failed: "Couldn't save that edit — please try again.",
  answer_route_failed:
    "Couldn't pass your answer on right now — please try again in a moment.",
  servant_turn_failed: "Loom couldn't answer that just now — please send it again.",
  servant_turn_timeout:
    "Loom's answer took too long and was stopped; your message is kept for its workspace.",
  model_key_rejected: "The API key was refused.",
  sandbox_terminated:
    "This session has stopped running. Use Restart above to start a new one.",
  assistant_not_configured:
    "This project's chat assistant isn't set up yet. Please contact your Loom admin.",
  ...MEETING_USER_ERROR_FALLBACK_EN,
};

// ---------------------------------------------------------------------------
// A failed apply.
//
// `readiness_apply_result` reports the Accept's write. On a failure it carries
// one of these in `code` and, in `error`, that code's static English led by the
// code (`applyErrorWireText`): the dashboard's card and an older Focus branch on
// the head token (`edit_pending`, `card_already_applied`) and print the rest.
// Focus renders DE/EN catalog copy for the code (`errorCopy.ts`). The apply's
// own exception — a GitLab error body, a URL, the agent-facing recovery prose —
// stays in the server's request log.
//
// Every code but `apply_failed` is a reason `executeApplyUpdateIssue`
// (`packages/mcp-server/src/tools/apply-update-issue.ts`) throws as the head of
// its message; `apply_failed` is anything else.
// ---------------------------------------------------------------------------

export const APPLY_ERROR_CODES = [
  // Anything without a reason of its own: a GitLab read or write that failed.
  "apply_failed",
  // A ready-screen edit is not folded into the card yet. Retry works.
  "edit_pending",
  // This conversation already wrote the issue once. Retry cannot work.
  "card_already_applied",
  // The token did not verify: expired, the card regressed, or it moved.
  "accept_token_unverified",
  // No verified card behind the write (none, or evicted / reset).
  "accept_token_required",
  "card_lost",
  // The verified card would not rebuild into an issue body.
  "reconstruct_failed",
  // The project's access gate refused, or could not answer.
  "access_denied",
] as const;

export type ApplyErrorCode = (typeof APPLY_ERROR_CODES)[number];

const APPLY_CODE_SET: ReadonlySet<string> = new Set(APPLY_ERROR_CODES);

export const isApplyErrorCode = (v: unknown): v is ApplyErrorCode =>
  typeof v === "string" && APPLY_CODE_SET.has(v);

/** Each apply code's static English: the text after the code in the wire
 *  `error`, what a surface without a catalog prints. */
export const APPLY_ERROR_FALLBACK_EN: Readonly<Record<ApplyErrorCode, string>> = {
  apply_failed: "Loom couldn't save the ticket. Please try again.",
  edit_pending:
    "Your change isn't in the ticket yet. Wait a moment or keep the previous value, then accept again.",
  card_already_applied:
    "This ticket was already saved once in this conversation, so the newer version wasn't saved. Change it on the ticket's page, or refine it in a new conversation.",
  accept_token_unverified:
    "The check on this ticket is out of date, so it wasn't saved. Refine it again, then accept.",
  accept_token_required:
    "Loom no longer has the checked version of this ticket. Refine it again, then accept.",
  card_lost:
    "Loom no longer has the checked version of this ticket. Refine it again, then accept.",
  reconstruct_failed:
    "Loom couldn't put the ticket text together, so nothing was saved. Refine it again, then accept.",
  access_denied:
    "Loom couldn't confirm that you may change tickets in this project. Try again, or ask whoever looks after Loom for you.",
};

/** The head of a message when it reads like a code: `token` or `token: …`. */
const headToken = (cause: unknown): string | null => {
  const message =
    cause instanceof Error ? cause.message : typeof cause === "string" ? cause : "";
  const m = /^([a-z][a-z0-9_]{0,63})(?::|$)/u.exec(message);
  return m === null ? null : m[1]!;
};

/**
 * The closed code for a failed apply: the reason at the head of the thrown
 * message (or of an older server's wire `error`) when it is one, else
 * `apply_failed`. Reads the head token only, never the text behind it.
 */
export const applyErrorCodeOf = (cause: unknown): ApplyErrorCode => {
  const head = headToken(cause);
  return isApplyErrorCode(head) ? head : "apply_failed";
};

/** The wire `error` of a failed apply: the code, then its static English. */
export const applyErrorWireText = (code: ApplyErrorCode): string =>
  `${code}: ${APPLY_ERROR_FALLBACK_EN[code]}`;

// ---------------------------------------------------------------------------
// A failed split.
//
// `readiness_breakdown_result.error` IS the code: it always was a token the
// client branches on (`breakdown.failed.stale` reads `token_unverified`,
// `parent_not_applied`, `criteria_stale`), except where an exception's message
// was passed through. Those are `parent_apply_failed` and `apply_failed` now,
// and the persisted `failureReason` the bridge re-emits holds the same code.
// ---------------------------------------------------------------------------

export const BREAKDOWN_ERROR_CODES = [
  // Anything without a reason of its own: a GitLab write that failed mid-run.
  "apply_failed",
  "no_store",
  // `verifyBreakdownToken` (`BreakdownTokenError`).
  "no_breakdown",
  "no_children_hash",
  "breakdown_token_unverified",
  "criteria_stale",
  "criteria_missing",
  // The parent's own apply threw, or did not land.
  "parent_apply_failed",
  "parent_not_applied",
  "breakdown_claim_conflict",
  "breakdown_row_lost",
  // A run that died mid-loop, found on the next bridge attach.
  "interrupted",
] as const;

export type BreakdownErrorCode = (typeof BREAKDOWN_ERROR_CODES)[number];

const BREAKDOWN_CODE_SET: ReadonlySet<string> = new Set(BREAKDOWN_ERROR_CODES);

export const isBreakdownErrorCode = (v: unknown): v is BreakdownErrorCode =>
  typeof v === "string" && BREAKDOWN_CODE_SET.has(v);

/** The closed code for a failed split: the message itself when it is one (a
 *  token thrown inside the run), else `apply_failed`. */
export const breakdownErrorCodeOf = (cause: unknown): BreakdownErrorCode => {
  const message =
    cause instanceof Error ? cause.message : typeof cause === "string" ? cause : "";
  return isBreakdownErrorCode(message) ? message : "apply_failed";
};
